import api from './api';

// Generate a unique session ID per browser tab/session
const SESSION_ID = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

let lastTrackedPath = '';

/**
 * Track a page visit or user action
 */
export const trackActivity = async (
  actionType: string,
  path?: string,
  details?: Record<string, any>
) => {
  try {
    await api.post('/activity/track/', {
      action_type: actionType,
      path: path || window.location.pathname,
      details: details || {},
      session_id: SESSION_ID,
    });
  } catch (error) {
    // Silently fail - don't break the app for tracking
    console.debug('[ActivityTracker] Failed to track:', error);
  }
};

/**
 * Track page visit - call on route change
 * De-duplicates if same path is visited consecutively
 */
export const trackPageVisit = (path: string, pageName?: string) => {
  if (path === lastTrackedPath) return;
  lastTrackedPath = path;
  
  trackActivity('page_visit', path, { page_name: pageName });
};

/**
 * Track specific user actions
 */
export const trackProductView = (productId: number, productName: string) => {
  trackActivity('product_view', `/products/${productId}`, {
    product_id: productId,
    product_name: productName,
  });
};

export const trackAddToCart = (productId: number, productName: string, quantity: number) => {
  trackActivity('add_to_cart', '/cart', {
    product_id: productId,
    product_name: productName,
    quantity,
  });
};

export const trackRemoveFromCart = (productId: number, productName: string) => {
  trackActivity('remove_from_cart', '/cart', {
    product_id: productId,
    product_name: productName,
  });
};

export const trackPlaceOrder = (orderId: number, orderNumber: string, totalAmount: number) => {
  trackActivity('place_order', `/orders/${orderId}`, {
    order_id: orderId,
    order_number: orderNumber,
    total_amount: totalAmount,
  });
};

export const trackViewOrder = (orderId: number, orderNumber: string) => {
  trackActivity('view_order', `/orders/${orderId}`, {
    order_id: orderId,
    order_number: orderNumber,
  });
};

export const trackViewReports = () => {
  trackActivity('view_reports', '/reports');
};

export const trackExportData = (exportType: string) => {
  trackActivity('export_data', '/reports', { export_type: exportType });
};

export const trackLogout = () => {
  trackActivity('logout', '/logout');
};

/**
 * Activity Log Admin API (admin-only)
 */
export const activityLogAPI = {
  getLogs: (params?: Record<string, string>) => 
    api.get('/activity/logs/', { params }),
  
  getSummary: () => 
    api.get('/activity/logs/summary/'),
  
  getActionTypes: () => 
    api.get('/activity/logs/action_types/'),
  
  resolveGeo: () => 
    api.post('/activity/logs/resolve_geo/'),
  
  getMyLastLogin: () => 
    api.get('/activity/my-last-login/'),
};
